import {
  Button,
  CaretDownIcon,
  CaretUpIcon,
  Dropdown,
  DropdownMenu,
  DropdownOption,
  TextInput,
} from 'datocms-react-ui';

import type {
  AITextBaseTextScope,
  ControlConfiguration,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { GraphicsPromptOption } from '@src/entrypoints/ControlGenerator/CompositionControlsGenerateService';

export type AITextConfigPatch = {
  promptId?: string;
  promptTitle?: string;
  defaultBaseTextScope?: AITextBaseTextScope;
};

type AITextControlSelectorProps = {
  control: ControlConfiguration & { enabled?: boolean };
  onUpdateControlType: (value: 'text' | 'aiText') => void;
  onUpdateControlId: (id: string) => void;
  onUpdateAITextConfig: (patch: AITextConfigPatch) => void;
  /**
   * `ai_prompt` records with `prompt_type: "Graphics"`, or `null` while they
   * are loading / after the load failed — which must not be reported as "none
   * exist".
   */
  graphicsPrompts: GraphicsPromptOption[] | null;
  /**
   * Whether the fetch actually failed. Distinguishes the two `null` cases:
   * loading says nothing, a failure says so.
   */
  promptsLoadFailed: boolean;
};

const BASE_TEXT_OPTIONS: { value: AITextBaseTextScope; label: string }[] = [
  { value: 'selection', label: 'Selection' },
  { value: 'clip', label: 'Clip' },
];

export function AITextControlSelector({
  control,
  onUpdateControlType,
  onUpdateControlId,
  onUpdateAITextConfig,
  graphicsPrompts,
  promptsLoadFailed,
}: AITextControlSelectorProps) {
  const isAIText = control.type === 'aiText';
  const promptId = isAIText ? control.promptId : undefined;
  const selectedPrompt = graphicsPrompts?.find(
    prompt => prompt.id === promptId,
  );
  const baseText = (isAIText && control.defaultBaseTextScope) || 'selection';
  const idIsMissing = !control.id?.trim();

  return (
    <>
      <Dropdown
        renderTrigger={({ open, onClick }) => (
          <Button
            buttonSize="xxs"
            type="button"
            onClick={onClick}
            rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
          >
            <span>{isAIText ? 'AI Text' : 'Text'}</span>
          </Button>
        )}
      >
        <DropdownMenu>
          <DropdownOption
            active={!isAIText}
            onClick={() => onUpdateControlType('text')}
          >
            Text
          </DropdownOption>
          <DropdownOption
            active={isAIText}
            onClick={() => onUpdateControlType('aiText')}
          >
            AI Text
          </DropdownOption>
        </DropdownMenu>
      </Dropdown>

      {isAIText && promptsLoadFailed && (
        <span style={{ fontSize: '12px', color: 'var(--alert-color)' }}>
          Couldn&apos;t load the Graphics AI Prompts — reload the page to pick a
          prompt. Saving leaves this control&apos;s prompt as it is.
        </span>
      )}

      {isAIText && graphicsPrompts?.length === 0 && (
        <span style={{ fontSize: '12px', color: 'var(--alert-color)' }}>
          No AI Prompt records with prompt type &quot;Graphics&quot; exist in
          this environment — create one in Content &rarr; AI Prompts first.
        </span>
      )}

      {isAIText && !!graphicsPrompts?.length && (
        <Dropdown
          renderTrigger={({ open, onClick }) => (
            <Button
              buttonSize="xxs"
              type="button"
              onClick={onClick}
              rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
            >
              <span
                style={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedPrompt?.title ?? 'Select Prompt'}
              </span>
            </Button>
          )}
        >
          <DropdownMenu>
            {graphicsPrompts?.map(prompt => (
              <DropdownOption
                key={prompt.id}
                active={prompt.id === promptId}
                onClick={() =>
                  onUpdateAITextConfig({
                    promptId: prompt.id,
                    promptTitle: prompt.title,
                  })
                }
              >
                {prompt.title}
              </DropdownOption>
            ))}
          </DropdownMenu>
        </Dropdown>
      )}

      {isAIText && (
        <Dropdown
          renderTrigger={({ open, onClick }) => (
            <Button
              buttonSize="xxs"
              type="button"
              onClick={onClick}
              rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
            >
              <span>
                Base text:{' '}
                {BASE_TEXT_OPTIONS.find(option => option.value === baseText)
                  ?.label ?? 'Selection'}
              </span>
            </Button>
          )}
        >
          <DropdownMenu>
            {BASE_TEXT_OPTIONS.map(option => (
              <DropdownOption
                key={option.value}
                active={option.value === baseText}
                onClick={() =>
                  onUpdateAITextConfig({ defaultBaseTextScope: option.value })
                }
              >
                {option.label}
              </DropdownOption>
            ))}
          </DropdownMenu>
        </Dropdown>
      )}

      {isAIText && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            maxWidth: '220px',
          }}
        >
          <TextInput
            id={`${control.label}-ai-text-id`}
            value={control.id ?? ''}
            onChange={onUpdateControlId}
            placeholder="Control id (required)"
          />
          {idIsMissing && (
            <span style={{ fontSize: '12px', color: 'var(--alert-color)' }}>
              An id is required — generated options are keyed by it.
            </span>
          )}
        </div>
      )}
    </>
  );
}
